import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import type { Market } from "@/types/stock"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// 시장 구분 뱃지 색상 (종목 목록, 차트 헤더 공용)
export function marketBadgeClassName(market: Market): string {
  switch (market) {
    case "KOSPI":
      return "border-blue-500/40 bg-blue-500/10 text-blue-400"
    case "KOSDAQ":
      return "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
    case "ETF":
    case "ETN":
      return "border-amber-500/40 bg-amber-500/10 text-amber-400"
    default:
      return "border-zinc-500/40 bg-zinc-500/10 text-zinc-400"
  }
}

// 종목 차트 페이지를 새 창에서 열기 (현재 화면 상태는 그대로 유지)
// features("popup,…")를 주는 이유: 없으면 "새 탭 요청"이라 설치형 앱(PWA) 창에서 연 차트가 앱 창이 아니라 일반 브라우저로 열린다.
// 크기는 작업 영역(작업표시줄 제외 화면)에 꽉 채워 열고, 열린 뒤에는 사용자가 창 크기·위치를 자유롭게 바꿀 수 있다(고정 아님, 기억하지 않음).
// noopener를 쓰지 않는다: 같은 사이트의 페이지라 보안상 필요 없고, noopener로 열면 차트 창의 뒤로가기 버튼이
// 창을 닫지 못해 "이전 탭으로 돌아가기"가 동작하지 않는다 (스크립트로 연 창만 window.close()로 닫을 수 있다).
function chartWindowFeatures() {
  const s = window.screen as Screen & { availLeft?: number; availTop?: number } // availLeft·availTop은 크로미움 전용
  return `popup,width=${s.availWidth},height=${s.availHeight},left=${s.availLeft ?? 0},top=${s.availTop ?? 0}`
}

// iPad(사파리·홈 화면 앱)는 원래 문제가 없었으므로 기존 호출(features 없음)을 그대로 쓴다. iPadOS는 데스크탑 UA를 보내서 터치 지점 수로 구분한다.
function isIPad() {
  return /iPad/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
}

export function openChartUrl(url: string) {
  window.open(url, "_blank", isIPad() ? undefined : chartWindowFeatures())
}

export function openStockChart(stockCode: string) {
  openChartUrl(`/chart/${stockCode}`)
}

// <a target="_blank"> 링크용: 그냥 클릭하면 스크립트로 열어서(차트 탭의 뒤로가기가 탭을 닫을 수 있게) 열고,
// Ctrl/Cmd/Shift 클릭이나 휠 클릭은 브라우저 기본 동작(백그라운드 탭 등)에 맡긴다.
export function openChartOnClick(e: { button: number; ctrlKey: boolean; metaKey: boolean; shiftKey: boolean; preventDefault: () => void }, url: string) {
  if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey) return
  e.preventDefault()
  openChartUrl(url)
}

// 천 단위 콤마 포맷 (차트 축/툴팁, 테이블 셀 공용)
export function formatNumber(value: unknown): string {
  if (typeof value === "number") return value.toLocaleString()
  return String(value ?? "")
}
